import * as THREE from "three";
import { ACUNIT_OBSTRUCTION, CHIMNEY_OBSTRUCTION, MUMTY_OBSTRUCTION, WATERTANK_OBSTRUCTION, HEIGHT_PER_FLOOR, OUT_OF_GROUND_ERROR } from "../utilities/constants";
import { convertVectorToArray, deg2Rad } from "../utilities/utils";
import BaseObject from "./BaseObject";
import { DRAW_OBSTRUCTION } from "../../pages/studio/constants";
import { COLOR_MAPPINGS } from "../utilities/visualConstants";
import * as utils from '../utilities/utils';
import * as raycastingUtils from '../utilities/rayCastingUtils';
import {
    LESS_VERTICES_THAN_NEEDED_FOR_PLACING_ERROR,
    LESS_VERTICES_THAN_NEEDED_FOR_DRAWING_ERROR,
} from '../utilities/constants';
import PolygonMeasurement from "../subObjects/PolygonMeasurement";
import studioDataService from '../../services/studioDataService';

const MINIMUM_NUMBER_OF_POINTS = 3;

export default class Obstruction extends BaseObject {
    constructor(stage, coreHeight, type, id) {
        super(stage);
        this.stage = stage;
        this.scale = 3;
        this.angle = 90;
        this.coreHeight = coreHeight;
        this.obstructionBaseHeight = this.stage.floorValue * HEIGHT_PER_FLOOR;
        this.baseHeight = this.obstructionBaseHeight + this.coreHeight / 2;
        // this.name = `Model #${this.id.toString()}`;
        this.id = id ? id : this.stage.getObsId();
        this.obstructionType = type;
        this.objectsGroup = new THREE.Group();
        this.objectsGroup.container = this;
        this.vertices = [];
        this.objectLastPosition = [];
        this.objectLastScale = [];
        this.objectLastAngle = [];
        this.polygonMeasurement = null;
        this.measurementTextMesh = [];
        // 2D: semi-transparent with original darker tone; 3D: solid with original lighter tone
        const OBSTRUCTION_COLOR_2D = 0x333333;   // previous 2D color
        const OBSTRUCTION_COLOR_3D = 0xCCCCCC;   // previous 3D color
        this.material2D = new THREE.MeshBasicMaterial({
            color: OBSTRUCTION_COLOR_2D,
            transparent: true,
            opacity: 0.60,
        });
        this.material3D = new THREE.MeshBasicMaterial({
            color: OBSTRUCTION_COLOR_3D,
            transparent: false,
        });
        if (type === MUMTY_OBSTRUCTION) {
            this.createMumty(3);
        } else if (type === ACUNIT_OBSTRUCTION) {
            this.createAcUnit(3);
        } else if (type === CHIMNEY_OBSTRUCTION) {
            this.createChimney(3);
        } else if (type === WATERTANK_OBSTRUCTION) {
            this.createWaterTank(3);
        }
        this.stage.sceneManager.scene.add(this.objectsGroup);
    }

    // Drawing functions
    initDrawingMode() {
        // Initialize drawing by providing event handlers and mesh materials
        this.stage.drawManager.initialize(
            this,
            this.onComplete.bind(this),
            this.onCancel.bind(this),
        );
    }

    setParentOnCreation() {
        const heightOffset = this.coreHeight / 2;
        // update new parent and snap to the surface to avoid floating
        const onRoof = this.stage.polygonModel && this.stage.areOverlapping(this);
        if (onRoof) {
            this.updateHeight((this.stage.floorValue * HEIGHT_PER_FLOOR) + heightOffset, this.stage.polygonModel);
        } else {
            // ground z assumed 0; snap obstruction base to ground level
            this.updateHeight(heightOffset, this.stage.ground);
        }
        // Ensure z exactly matches the chosen parent's base for visual consistency
        this.mesh.position.z = this.baseHeight;
    }

    createMumty(scaleValue) {
        this.geometry = new THREE.BoxGeometry(scaleValue, scaleValue, this.coreHeight);
        this.mesh = new THREE.Mesh(
            this.geometry,
            this.material2D,
        );
        this.mesh.position.set(0, 0, this.obstructionBaseHeight + this.coreHeight / 2);
        // this.vertices = [
        //     new THREE.Vector3(-1.5, 1.5),
        //     new THREE.Vector3(1.5, 1.5),
        //     new THREE.Vector3(1.5, -1.5),
        //     new THREE.Vector3(-1.5, -1.5),
        // ];
        this.initVertices = [
            new THREE.Vector3(-1.5, 1.5),
            new THREE.Vector3(1.5, 1.5),
            new THREE.Vector3(1.5, -1.5),
            new THREE.Vector3(-1.5, -1.5),
        ];

        this.transformedVertices = this.getVectorVertices();

        this.polygonMeasurement = new PolygonMeasurement(
            [...this.transformedVertices],
            this, this.stage,
        );

        this.objectsGroup.add(this.mesh);
        // this.stage.addToObjectsArray(this);
        this.stage.lastChangedObject.push([this, 'pos']);
        this.objectLastPosition.push({ ...this.mesh.position });
        this.setParentOnCreation();
        this.onComplete();
    }
    
    getVectorVertices() {
        const arrayVertices = this.get3DVertices();
        if ([WATERTANK_OBSTRUCTION, CHIMNEY_OBSTRUCTION].includes(this.obstructionType)) {
            return [new THREE.Vector3(...arrayVertices[0]), new THREE.Vector3(this.mesh.position.x, this.mesh.position.y, arrayVertices[0][2])]
        }
        return arrayVertices.map((ele) => new THREE.Vector3(ele[0], ele[1], ele[2]));
    }

    getVertices() {
        const transformed = this.initVertices.map(v => 
            v.clone()
                .applyMatrix4(this.mesh.matrixWorld) // Then apply mesh's world matrix
        );
        return convertVectorToArray(transformed);
    }
    
    get3DVertices() {
        const vectorVertices = utils.convertArrayToVector(this.getVertices())
        const heightOffset = this.coreHeight / 2;
        // assign height to the initPoints
        const vertices = []
        
        // Check if polygonModel exists before calling areOverlapping
        const isOverlapping = this.stage.polygonModel ? this.stage.areOverlapping(this) : false;
        
        for (let i = 0; i < vectorVertices.length; i++) {
            vertices.push(new THREE.Vector3(
                vectorVertices[i].x,
                vectorVertices[i].y,
                isOverlapping ? this.baseHeight + heightOffset : heightOffset,
            ))
        }
        return vertices.map(ele => [ele.x, ele.y, ele.z])
    }

    getAvailableArea() {
        const vertices = this.getVertices();
        const modVertices = vertices.map(vertex => ({
            x: vertex[0],
            y: vertex[1],
        }));
        const area = Math.abs(THREE.ShapeUtils.area(modVertices) / Math.cos((this.tilt || 0 * Math.PI) / 180))
        if (area && !isNaN(area)) return area;
        else return 0;
    }

    createChimney(scaleValue) {
        this.geometry = new THREE.CylinderGeometry(scaleValue, scaleValue, this.coreHeight, 32).rotateX(Math.PI / 2);
        this.mesh = new THREE.Mesh(this.geometry, this.material2D);
        this.mesh.position.set(0, 0, this.obstructionBaseHeight + this.coreHeight / 2);
        this.objectsGroup.add(this.mesh);
        // this.stage.addToObjectsArray(this);
        this.stage.lastChangedObject.push([this, 'pos']);
        this.objectLastPosition.push({ ...this.mesh.position });
        // this.vertices = this.getCirclePoints(32, 3, this.mesh.position.clone());
        this.initVertices = this.getCirclePoints(32, 3, this.mesh.position.clone());
        // this.stage.addToObjectsArray(this);
        this.transformedVertices = this.getVectorVertices();

        this.polygonMeasurement = new PolygonMeasurement(
            [...this.transformedVertices],
            this, this.stage,
        );
        this.setParentOnCreation();
        this.onComplete();
    }

    createAcUnit(scaleValue) {
        this.geometry = new THREE.BoxGeometry(scaleValue, scaleValue, this.coreHeight);
        this.mesh = new THREE.Mesh(
            this.geometry,
            this.material2D,
        );
        this.mesh.position.set(0, 0, this.obstructionBaseHeight + this.coreHeight / 2);
        this.objectsGroup.add(this.mesh);
        // this.vertices = [
        //     new THREE.Vector3(-1.5, 1.5),
        //     new THREE.Vector3(1.5, 1.5),
        //     new THREE.Vector3(1.5, -1.5),
        //     new THREE.Vector3(-1.5, -1.5),
        // ];
        this.initVertices = [
            new THREE.Vector3(-1.5, 1.5),
            new THREE.Vector3(1.5, 1.5),
            new THREE.Vector3(1.5, -1.5),
            new THREE.Vector3(-1.5, -1.5),
        ];
        // this.stage.addToObjectsArray(this);
        this.transformedVertices = this.getVectorVertices();

        this.polygonMeasurement = new PolygonMeasurement(
            [...this.transformedVertices],
            this, this.stage,
        );
        this.stage.lastChangedObject.push([this, 'pos']);
        this.objectLastPosition.push({ ...this.mesh.position });
        this.setParentOnCreation();
        this.onComplete();
    }

    createWaterTank(scaleValue) {
        this.geometry = new THREE.CylinderGeometry(scaleValue, scaleValue, this.coreHeight, 32).rotateX(Math.PI / 2);
        this.mesh = new THREE.Mesh(this.geometry, this.material2D);
        this.mesh.position.set(0, 0, this.obstructionBaseHeight + this.coreHeight / 2);
        this.objectsGroup.add(this.mesh);
        // this.stage.addToObjectsArray(this);
        this.stage.lastChangedObject.push([this, 'pos']);
        this.objectLastPosition.push({ ...this.mesh.position });
        // this.vertices = this.getCirclePoints(32, 3, this.mesh.position.clone());
        this.initVertices = this.getCirclePoints(32, 3, this.mesh.position.clone());
        this.transformedVertices = this.getVectorVertices();

        this.polygonMeasurement = new PolygonMeasurement(
            [...this.transformedVertices],
            this, this.stage,
        );

        this.setParentOnCreation();
        this.onComplete();
    }
    /**
     * It takes a number of segments, a radius, and a center point and returns an array of points that are
     * evenly spaced around the circumference of a circle.
     * @param segment - number of points to draw
     * @param radius - The radius of the circle.
     * @param center - the center of the circle
     * @returns An array of Vector2 objects.
    */
    getCirclePoints(segment, radius, center) {
        let arr = [];
        let slice = 2 * Math.PI / segment;
        for (let i = 0; i < segment; i++) {
            let angle = slice * i;
            let newX = (center.x + radius * Math.cos(angle));
            let newY = (center.y + radius * Math.sin(angle));
            let p = new THREE.Vector3(newX, newY);
            arr.push(p);
        }
        return arr;
    }

    onComplete(geometry) {
        if (this.obstructionType === DRAW_OBSTRUCTION) {
            // getting vertices from buffer geometry
            const vertices = [];
            for (let i = 0; i < geometry.noOfVertices; i += 1) {
                vertices.push(new THREE.Vector3(
                    geometry.attributes.position.array[(i * 3)],
                    geometry.attributes.position.array[(i * 3) + 1],
                    1,
                ));
            }

            const jstsGeom = utils.verticesToJSTSPolygon(vertices.map(v => [v.x, v.y])).buffer(0);
            const simplifiedVertices = utils.verticesFromJSTSPolygon(jstsGeom).map(v => new THREE.Vector3(...v));

            // Removing collinear vertices
            for (let i = 0; i < simplifiedVertices.length; i += 1) {
                const vertex = simplifiedVertices[i];
                const vertexNext = simplifiedVertices[(i + 1) % simplifiedVertices.length];
                const vertexPrev = simplifiedVertices[(i - 1 + simplifiedVertices.length) % simplifiedVertices.length];
                if (utils.checkCollinear(vertex, vertexNext, vertexPrev)) {
                    simplifiedVertices.splice(i, 1);
                    i -= 1;
                }
            }

            const vertices2D = [];
            for (let i = 0, l = simplifiedVertices.length; i < l; i += 1) {
                vertices2D.push(new THREE.Vector3(simplifiedVertices[i].x, simplifiedVertices[i].y, 1))
            }

            geometry.computeBoundingSphere();

            this.initVertices = vertices2D;
            this.updatedVertices = vertices2D;
            const coreShape = new THREE.Shape(vertices2D);
            const coreGeometry = new THREE.ExtrudeGeometry(
                coreShape, {
                    depth: this.coreHeight,
                    bevelEnabled: false,
                },
            );

            this.geometry = coreGeometry;
            this.mesh = new THREE.Mesh(this.geometry, this.material2D);
            this.mesh.position.set(0, 0, this.obstructionBaseHeight);
            this.objectsGroup.add(this.mesh);
            this.centerGeometry(this.mesh, true);
            this.transformedVertices = this.getVectorVertices();

            this.polygonMeasurement = new PolygonMeasurement(
                [...this.transformedVertices],
                this, this.stage,
            );
            this.setParentOnCreation();
            this.addObstructionInLocal();
            this.stage.lastChangedObject.push([this, 'pos']);
            this.objectLastPosition.push({ ...this.mesh.position });
            // Enable undo since draw obstruction is now in history
            this.stage.studioStore.$patch({ 
                undoEnabled: true,
                redoEnabled: false 
            });
        }
        this.stage.deselectOtherSelectedMeshes(this);
        this.stage.currentSelectedObject = this;
        this.updateCoreEdges();
    }
    
    centerGeometry(mesh, created = false) {
        // Compute the bounding box of the geometry
        mesh.geometry.computeBoundingBox();
        const boundingBox = mesh.geometry.boundingBox;
        
        // Calculate the center of the bounding box
        const center = new THREE.Vector3();
        boundingBox.getCenter(center);
        
        // Translate geometry so its center is at origin
        mesh.geometry.translate(-center.x, -center.y, -center.z);
        
        // // Update the mesh position to maintain world position
        mesh.position.add(center);
        
        // // Important: Update the bounding box after transformation
        mesh.geometry.computeBoundingBox();

        if (created) {
            this.initVertices = this.initVertices.map(v => v.sub(center));
        }
    }

    get2DVertices() {
        const vertices = [];
        for (let i = 0, len = this.transformedVertices.length; i < len; i += 1) {
            vertices.push([
                this.transformedVertices[i].x,
                this.transformedVertices[i].y,
            ]);
        }
        return vertices;
    }

    onCancel() {
        // Remove parent - child relationship
        if (this.getParent() !== null) {
            this.getParent().removeChild(this);
        }

        // Remove from scene
        this.stage.sceneManager.scene.remove(this.objectsGroup);
    }

    getColorMap() {
        const colorMapping = COLOR_MAPPINGS.POLYGON;
        if (this.materialAndVisualStatesExist(colorMapping)) {
            return colorMapping[this.materialState][this.visualState];
        }
        return {};
    }

    getPlacingInformation(vertices) {
        const response = {};
        let numberOfPoints;

        // Getting vertices
        let vertices2DArray;
        if (vertices === null || vertices === undefined) {
            vertices2DArray = this.initVertices;
            numberOfPoints = vertices2DArray.length;
        } else {
            vertices2DArray = vertices;
            numberOfPoints = vertices2DArray.length - 1;
        }
        let parentExists = true;
        let polygonExists = true;
        response.errors = [];
        // This is the error that is displayed to the user
        response.pointUnplaceableError = null;

        const vertices2DVectorArray = utils.convertArrayToVector(vertices2DArray);
        if (!raycastingUtils.areVerticesOnGround(vertices2DVectorArray, this.stage)) {
            const error = new Error(OUT_OF_GROUND_ERROR);
            response.errors.push(error);
            parentExists = false;
            response.pointUnplaceableError = error;
        }
        if (numberOfPoints < MINIMUM_NUMBER_OF_POINTS) {
            const error = new Error(LESS_VERTICES_THAN_NEEDED_FOR_PLACING_ERROR);
            response.cannotCompleteError = error;
            response.errors.push(error);
        }
        if (numberOfPoints + 1 < MINIMUM_NUMBER_OF_POINTS) {
            const error = new Error(LESS_VERTICES_THAN_NEEDED_FOR_DRAWING_ERROR);
            response.errors.push(error);
            response.cannotCompleteError = error;
            parentExists = false;
            polygonExists = false;
        }
        // if (vertices2DArray.slice(0, numberOfPoints).length > MINIMUM_NUMBER_OF_POINTS &&
        //     utils.checkComplexGeometry(vertices2DArray.slice(0, numberOfPoints))) {
        //     const error = new Error(COMPLEX_GEOMETRY_ERROR);
        //     response.errors.push(error);
        //     response.cannotCompleteError = error;
        //     parentExists = false;
        // }
        return response;
    }

    getObjectType(){
        return "Obstruction";
    }

    addObstructionInLocal(){
        let stageData = studioDataService.getStageData();
        if(stageData.obstructionData){
            const isSameId = (element) => element.id == this.id;
            let indInArr = stageData.obstructionData.findIndex(isSameId);
            if(indInArr !== -1){
                stageData.obstructionData.splice(indInArr, 1);
            }
            stageData.obstructionData.push({
                centerPoint: {x:this.getPosition().x, y: this.getPosition().y},
                baseHeight: this.baseHeight,
                scale: this.scale,
                angle: this.angle,
                type: this.obstructionType,
                id: this.id,
                vertices: this.initVertices.map(ele => [ele.x, ele.y, ele.z]),
                coreHeight: this.coreHeight
            });
        }
        stageData.modelId = this.stage._MODEL_ID_INIT;
        stageData.obsId = this.stage._OBS_ID_INIT;
        studioDataService.setStageData(stageData);
    }

    removeObstructionFromLocal(){
        let stageData = studioDataService.getStageData();
        if(stageData.obstructionData){
            const isSameId = (element) => element.id == this.id;
            let indInArr = stageData.obstructionData.findIndex(isSameId);
            if(indInArr !== -1){
                stageData.obstructionData.splice(indInArr, 1);
            }
        }
        studioDataService.setStageData(stageData);
    }

    loadObject(obsData) {
        if (this.obstructionType === DRAW_OBSTRUCTION) {
            const vertices2D = obsData.vertices.map(ele => {return new THREE.Vector3(ele[0], ele[1], 1)});

            this.initVertices = vertices2D;
            this.updatedVertices = vertices2D;
            const coreShape = new THREE.Shape(vertices2D);
            const coreGeometry = new THREE.ExtrudeGeometry(
                coreShape, {
                    depth: this.coreHeight,
                    bevelEnabled: false,
                },
            );

            this.geometry = coreGeometry;
            this.mesh = new THREE.Mesh(this.geometry, this.material2D);
            this.centerGeometry(this.mesh, false);
            this.transformedVertices = this.getVectorVertices();

            this.polygonMeasurement = new PolygonMeasurement(
                [...this.transformedVertices],
                this, this.stage,
            );
            this.coreEdges = new THREE.EdgesGeometry(coreGeometry.clone());
            this.line = new THREE.LineSegments(this.coreEdges, new THREE.LineBasicMaterial({ color: 0xFFFFFF }));
            this.objectsGroup.add(this.line, this.mesh);
            let parent = this.stage.ground;
            if (this.stage.areOverlapping(this)) {
                parent = this.stage.polygonModel;
            }
            this.changeParent(parent);
            this.addObstructionInLocal();
        }
        let diffVec = this.getPosition();
        this.moveObject(-diffVec.x+obsData.centerPoint.x, -diffVec.y+obsData.centerPoint.y);
        this.updateObstructionScale(obsData.scale);
        if(obsData.angle) this.rotateObstruction(obsData.angle);
        this.setParentOnCreation();
        this.polygonMeasurement.update(this.getVectorVertices());
    }

    storeLastPosition(position) {
        this.objectLastPosition.push({ ...position });
    }

    storeLastScale() {
        this.objectLastScale.push(this.scale);
    }

    storeLastAngle() {
        this.objectLastAngle.push(this.angle);
    }

    undo(type = 'undef') {
        // Check if this is undo of initial creation (no history beyond initial placement)
        if (this.objectLastPosition.length <= 1 && this.objectLastScale.length === 0 && this.objectLastAngle.length === 0) {
            // Removal due to undo of initial creation — preserve redo stack entries
            this.removeObject(true);
            // Return early since object is now removed and polygonMeasurement is null
            return;
        }
        if (type === 'pos') {
            if (this.objectLastPosition.length > 0) {
                let position = this.objectLastPosition.pop();
                let delta = this.mesh.position.clone().sub(position).negate();
                this.moveObject(delta.x, delta.y);
                this.placeObject();
                //this.mesh.position.set(position.x, position.y, position.z);
            }
        } else if (type === 'scale') {
            if (this.objectLastScale.length > 0) {
                let scale = this.objectLastScale.pop();
                this.updateObstructionScale(scale);
            }
        } else if (type === 'angle') {
            if (this.objectLastAngle.length > 0) {
                let angle = this.objectLastAngle.pop();
                this.rotateObstruction(angle);
            }
        }
        // Only update if object still exists
        if (this.polygonMeasurement) {
            this.deSelect();
            this.polygonMeasurement.update(this.getVectorVertices());
        }
    }
    // cube
     updateHeight(height = 0, newParent){
        //this.baseHeight =  ;
        // this.mesh.position.set(this.mesh.position.x, this.mesh.position.y, this.obstructionBaseHeight + this.scale / 2);
        // let currentPos = new THREE.Vector3(this.mesh.position.x, this.mesh.position.y, );
        // let newPos = new 
        // let delta = this.mesh.position.clone().sub(new THREE.Vector3(this.mesh.position.x, this.mesh.position.y, this.obstructionBaseHeight + this.scale / 2));
        this.moveObject(0 , 0,height - this.baseHeight);
        this.baseHeight = height;
        // update new parent
        this.changeParent(newParent);
        // update new base height
    }

    // scaleVertices(scale) {
    //     console.log('this.vertices: ',scale, [...this.vertices]);
    //     let vecScale = scale-3;
    //     let center = this.mesh.position.clone();
    //     center.z = 0;
    //     let vertices = this.vertices;
    //     let scaledVertices = [];
    //     for(let i = 0; i < vertices.length; i++) {
    //         var direction = new THREE.Vector3();
    //         direction.subVectors( vertices[i], center );
    //         if(vecScale > 0){
    //             direction.multiplyScalar(vecScale);
    //         }else{
    //             direction.negate();
    //             direction.multiplyScalar(vecScale);
    //         }
    //         scaledVertices.push(direction);
    //     }
    //     this.vertices = scaledVertices;
    //     console.log('scaled: ', scaledVertices);
    // }


    updateObstructionScale(scaleValue) {
        //this.scaleVertices(scaleValue);
        this.scale = scaleValue;
        scaleValue = scaleValue / 3;
        this.mesh.scale.set(scaleValue, scaleValue, 1);
        this.mesh.position.z = (this.baseHeight);
        this.placeObject();
        this.mesh.updateMatrixWorld();
        this.polygonMeasurement.update(this.getVectorVertices());
    }

    rotateObstruction(angle) {
        let angleToRotate = parseInt(angle)-parseInt(this.angle);
        this.angle = parseInt(angle);
        this.mesh.rotateZ(deg2Rad(angleToRotate));
        this.placeObject();
        this.mesh.updateMatrixWorld();
        this.polygonMeasurement.update(this.getVectorVertices());
    }

    moveObject(deltaX, deltaY, deltaZ = 0) {
        // this.baseHeight += deltaZ;
        this.mesh.applyMatrix4(new THREE.Matrix4().makeTranslation(deltaX, deltaY, deltaZ));
        if(this.line){
            this.line.applyMatrix4(new THREE.Matrix4().makeTranslation(deltaX, deltaY, deltaZ));
        }
        // for (let i = 0, l = this.vertices.length; i < l; i += 1) {
        //     this.vertices[i].x += deltaX;
        //     this.vertices[i].y += deltaY;
        //     this.vertices[i].z += deltaZ;
        // }
        this.mesh.updateMatrixWorld();
        this.polygonMeasurement.update(this.getVectorVertices());
    }

    placeObject(deltaX = 0, deltaY = 0, deltaZ = 0) {
        // let delta = this.mesh.getPosition().sub(new THREE.Vector3(deltaX, deltaY, deltaZ));
        // this.mesh.translateX(deltaX);
        // this.mesh.translateY(deltaY);
        // this.mesh.translateZ(deltaZ);
        // for (var i = 0; i < this.vertices.length; i++) {
        //     this.vertices[i].x += deltaX;
        //     this.vertices[i].y += deltaY;
        //     this.vertices[i].z += deltaZ;
        // }
        // get new parent and height while placing, snapping to the surface
        this.setParentOnCreation();
        // const newParent = stage.ground;
        // const newHeight = this.stage.ground.height;

        // // update new parent
        // this.changeParent(newParent);

        // // update new base height
        // this.baseHeight = newHeight;
        // this.updateGeometry();
        //this.deSelect();
        this.addObstructionInLocal();
        this.updateCoreEdges();
    }

    removeObject(preserveRedo = false) {
        this.polygonMeasurement.remove();
        const i = 0;
        while (this.getChildren().length > i) {
            this.getChildren()[i].removeObject();
        }

        // NOTE: deSelect should be after save since it will disable
        // drag controls and stop Undo/Redo container
        //select ground . 
        this.stage.ground.onSelect();
        this.stage.sceneManager.scene.remove(this.objectsGroup);
        let prevParent = null;
        if (this.getParent() !== null) {
            prevParent = this.getParent();
            this.getParent().removeChild(this);
            prevParent = null;
        }
        
        let cloneArray = [...this.stage.lastChangedObject];

        // temp stores the filtered array of obstructions that were deleted
        // it now has removed the positions of the deleted obstructions from the [lastChangedObject] array

        let temp = cloneArray.filter((ele) => {
            return ele[0].uuid !== this.uuid;
        });
        this.removeObstructionFromLocal();
        // update the lastChangedObject with filtered array
        this.stage.lastChangedObject = temp;
        // Also filter the removed obstruction from redoStack to prevent stale redo,
        // except when removal is triggered by Undo (initial creation undo) and we
        // must preserve the 'recreate' snapshot for Redo to work.
        if (!preserveRedo && this.stage.redoStack) {
            this.stage.redoStack = this.stage.redoStack.filter((ele) => ele[0]?.uuid !== this.uuid);
            this.stage.studioStore.$patch({ redoEnabled: this.stage.redoStack.length > 0 });
        }
        stage.obstructionInScene();
    }

    getPosition() {
        return this.mesh.position.clone();
    }

    onSelect() {
        this.stage.currentSelectedObject = this;
        this.updateCoreEdges();
        this.stage.changeSlider(this.scale);
        this.stage.changeRotationSlider(this.angle);

        this.polygonMeasurement.show();
        this.polygonMeasurement.update(this.getVectorVertices());
    }

    deSelect() {
        this.stage.currentSelectedObject = null;
        this.removeCoreEdges();

        this.polygonMeasurement.hide();
    }
    
    updateCoreEdges() {
        this.removeCoreEdges();
        this.coreEdges = new THREE.EdgesGeometry(this.geometry.clone().applyMatrix4(this.mesh.matrix));
        this.line = new THREE.LineSegments(this.coreEdges, new THREE.LineBasicMaterial({ color: 0xFFFFFF }));
        this.objectsGroup.add(this.line);
    }
    
    removeCoreEdges() {
        this.objectsGroup.remove(this.line);
    }
    
    switchTo2d() {
        this.mesh.material = this.material2D;
        //this.objectsGroup.remove(this.line);
    }

    switchTo3d() {
        this.mesh.material = this.material3D;
        this.updateCoreEdges();
    }

    saveObject() {
        // Return obstruction data in the format expected by the save system
        return {
            centerPoint: {x: this.getPosition().x, y: this.getPosition().y},
            baseHeight: this.baseHeight,
            scale: this.scale,
            angle: this.angle,
            type: this.obstructionType,
            id: this.id,
            vertices: this.initVertices.map(ele => [ele.x, ele.y, ele.z]),
            coreHeight: this.coreHeight
        };
    }
}
